import { Popper, type PopperProps } from '@mui/material';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLineColor } from '@/hooks';
import { chromeMenuItemClass, chromeMenuPaperClass } from '@/screens/FountainEditor/components/BlockChrome/ChromeMenu';
import type { ILine } from '@/types';

interface LineAutocompleteProps {
    lines: ILine[];
    anchorEl: PopperProps['anchorEl'];
    searchQuery: string;
    onSelect: (line: ILine) => void;
    onCreate: (title: string) => void;
    onClose: () => void;
}

export const LineAutocomplete = memo(({ lines, anchorEl, searchQuery, onSelect, onCreate, onClose }: LineAutocompleteProps) => {
    const { t } = useTranslation();
    const getLineColor = useGetLineColor();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef<HTMLUListElement>(null);

    const filteredLines = useMemo(
        () =>
            lines
                .filter(line => {
                    if (!searchQuery) return true;
                    const query = searchQuery.toLowerCase();
                    return line.title?.toLowerCase().includes(query) || line.code?.toLowerCase().includes(query);
                })
                .sort((a, b) => a.code.localeCompare(b.code))
                .slice(0, 10),
        [lines, searchQuery],
    );

    const hasExactMatch = useMemo(
        () =>
            lines.some(
                line => line.title?.toLowerCase() === searchQuery.toLowerCase() || line.code?.toLowerCase() === searchQuery.toLowerCase(),
            ),
        [lines, searchQuery],
    );

    const showCreateOption = searchQuery.trim() && !hasExactMatch;
    const totalItems = filteredLines.length + (showCreateOption ? 1 : 0);

    useEffect(() => {
        setSelectedIndex(0);
    }, []);

    const selectedIndexRef = useRef(selectedIndex);
    const filteredLinesRef = useRef(filteredLines);
    const totalItemsRef = useRef(totalItems);
    const showCreateOptionRef = useRef(showCreateOption);
    const searchQueryRef = useRef(searchQuery);

    useEffect(() => {
        selectedIndexRef.current = selectedIndex;
    }, [selectedIndex]);
    useEffect(() => {
        filteredLinesRef.current = filteredLines;
    }, [filteredLines]);
    useEffect(() => {
        totalItemsRef.current = totalItems;
    }, [totalItems]);
    useEffect(() => {
        showCreateOptionRef.current = showCreateOption;
    }, [showCreateOption]);
    useEffect(() => {
        searchQueryRef.current = searchQuery;
    }, [searchQuery]);

    useEffect(() => {
        if (!anchorEl) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(prev => Math.min(prev + 1, totalItemsRef.current - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(prev => Math.max(prev - 1, 0));
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (selectedIndexRef.current < filteredLinesRef.current.length) {
                        const selected = filteredLinesRef.current[selectedIndexRef.current];
                        if (selected) onSelect(selected);
                    } else if (showCreateOptionRef.current) {
                        onCreate(searchQueryRef.current.trim());
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    onClose();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [anchorEl, onSelect, onCreate, onClose]);

    useEffect(() => {
        if (listRef.current) {
            const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
            selectedElement?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selectedIndex]);

    if (!anchorEl || totalItems === 0) return null;

    return (
        <Popper
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            placement="bottom-start"
            modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
            sx={{ zIndex: 1300 }}
        >
            {/* QA §2.3.3: visual parity with the pill type-picker via
                shared <ChromeMenu> classes (radii, shadow, border, item
                padding, hover surface). */}
            <div
                data-bracket-popup
                className={`${chromeMenuPaperClass} w-60 max-h-[300px] overflow-auto`}
                onMouseDown={e => e.preventDefault()}
            >
                <ul ref={listRef} className="m-0 list-none p-0">
                    {filteredLines.map((line, index) => {
                        const color = getLineColor(line.color);
                        return (
                            // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard interaction handled by parent
                            <li key={line.id} className={chromeMenuItemClass(index === selectedIndex)} onClick={() => onSelect(line)}>
                                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                                <span>
                                    <span className="mr-1 font-semibold" style={{ color }}>
                                        {line.code}
                                    </span>
                                    {line.title}
                                </span>
                            </li>
                        );
                    })}
                    {showCreateOption && (
                        // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard interaction handled by parent
                        <li
                            className={`${chromeMenuItemClass(selectedIndex === filteredLines.length)} italic`}
                            onClick={() => onCreate(searchQuery.trim())}
                        >
                            {t('lines.create-button')} &ldquo;{searchQuery.trim()}&rdquo;
                        </li>
                    )}
                </ul>
            </div>
        </Popper>
    );
});

LineAutocomplete.displayName = 'LineAutocomplete';
